/**
 * Expanded detail body for a single tool call — rendered inside ToolLine's
 * indented hairline box, and inside SubAgentGroup's expanded summary. Branches
 * per tool; the generic fallback shows the pretty-printed input plus a
 * captioned Result/Error block.
 */
import { UnifiedDiffView } from '~/components/chat/UnifiedDiffView';
import type { ToolPart } from '~/components/chat/message-parts';
import { SUB_AGENT_TOOLS, toolNameOf } from './tool-meta';
import { normalizedFilePath } from './file-link';

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + '\n... (truncated)' : str;
}

/** Terminal result of a tool part, or null while it's still running. */
export function resultOf(part: ToolPart): { text: string; isError: boolean } | null {
  if (part.state === 'output-error') {
    return { text: part.errorText || 'Tool failed', isError: true };
  }

  if (part.state !== 'output-available') {
    return null;
  }

  const out = part.output;

  if (out == null) {
    return { text: '', isError: false };
  }

  return { text: typeof out === 'object' ? JSON.stringify(out, null, 2) : String(out), isError: false };
}

export function ResultBlock({ text, isError = false }: { text: string; isError?: boolean }) {
  return (
    <div>
      <div className={`ui-caption mb-1.5 ${isError ? 'text-danger' : ''}`}>{isError ? 'Error' : 'Result'}</div>
      <pre className="m-0 text-[12px] leading-[1.6] text-secondary font-geist-mono whitespace-pre-wrap break-all max-h-[280px] overflow-y-auto modern-scrollbar">
        {text || '(empty)'}
      </pre>
    </div>
  );
}

export function DiffBlock({ oldText, newText }: { oldText: string; newText: string }) {
  const oldLines = oldText ? oldText.split('\n') : [];
  const newLines = newText ? newText.split('\n') : [];

  return (
    <pre className="m-0 font-geist-mono text-[12px] leading-[1.6] whitespace-pre-wrap break-all max-h-[320px] overflow-y-auto modern-scrollbar rounded-lg border border-hairline">
      {oldLines.map((line, i) => (
        <div key={`o${i}`} className="px-3 code-diff-del">
          <span className="select-none opacity-60">- </span>
          {line}
        </div>
      ))}
      {newLines.map((line, i) => (
        <div key={`n${i}`} className="px-3 code-diff-add">
          <span className="select-none opacity-60">+ </span>
          {line}
        </div>
      ))}
    </pre>
  );
}

export function ToolDetail({ part }: { part: ToolPart }) {
  const name = toolNameOf(part);
  const result = resultOf(part);

  if (name === 'run_shell' || name === 'start_server') {
    return (
      <div className="flex flex-col gap-2">
        <pre className="m-0 rounded-lg bg-[#0A0A0E] border border-hairline px-3 py-2.5 font-geist-mono text-[13px] leading-[1.6] text-primary whitespace-pre-wrap break-all">
          <span className="text-accent select-none">❯ </span>
          {String(part.input?.command ?? '')}
        </pre>
        {result && <ResultBlock text={truncate(result.text, 2000)} isError={result.isError} />}
      </div>
    );
  }

  if (name === 'apply_diff' && part.input?.diff) {
    return (
      <div className="flex flex-col gap-2">
        <UnifiedDiffView diffStr={part.input.diff} filePath={part.input?.filePath} />
        {result?.isError && <ResultBlock text={result.text} isError />}
      </div>
    );
  }

  if (name === 'edit_file' && part.input?.oldString != null && part.input?.newString != null) {
    return (
      <div className="flex flex-col gap-2">
        {part.input?.filePath && (
          <div className="font-geist-mono text-[12px] text-muted break-all">
            {normalizedFilePath(part.input.filePath)}
          </div>
        )}
        <DiffBlock oldText={part.input.oldString} newText={part.input.newString} />
        {result?.isError && <ResultBlock text={result.text} isError />}
      </div>
    );
  }

  if (name === 'write_file') {
    const content = typeof part.input?.content === 'string' ? part.input.content : '';

    return (
      <div className="flex flex-col gap-2">
        {part.input?.filePath && (
          <div className="font-geist-mono text-[12px] text-muted break-all">
            {normalizedFilePath(part.input.filePath)}
          </div>
        )}
        {content && (
          <pre className="m-0 text-[12px] leading-[1.6] text-secondary font-geist-mono whitespace-pre-wrap break-all max-h-[280px] overflow-y-auto modern-scrollbar">
            {truncate(content, 5000)}
          </pre>
        )}
        {result?.isError && <ResultBlock text={result.text} isError />}
      </div>
    );
  }

  if (name === 'take_screenshot') {
    const out = part.output as any;

    if (out && typeof out === 'object' && out.base64) {
      return (
        <div className="flex flex-col gap-2">
          <img
            src={`data:image/png;base64,${out.base64}`}
            alt="Preview screenshot"
            className="max-w-full h-auto rounded-md border border-hairline"
          />
          <span className="text-[11px] text-muted font-geist-mono">
            {out.width}×{out.height}px
          </span>
        </div>
      );
    }

    if (result?.isError && result.text) {
      return <ResultBlock text={result.text} isError />;
    }

    return <div className="text-[12px] text-muted">{result ? 'Screenshot capture failed' : 'Capturing…'}</div>;
  }

  if (SUB_AGENT_TOOLS.has(name)) {
    const out = part.output as any;
    const rawSummary = typeof out === 'object' ? out?.summary || out?.diagnosis : String(out || '');
    const display =
      typeof rawSummary === 'string' && rawSummary.trim().length > 0 ? rawSummary : '(sub-agent returned no output)';

    return <ResultBlock text={truncate(display, 3000)} isError={part.state === 'output-error'} />;
  }

  if (name === 'http_request' && part.output && typeof part.output === 'object') {
    const out = part.output as any;
    const bodyStr = typeof out.body === 'object' ? JSON.stringify(out.body, null, 2) : String(out.body || '');

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-[12px] font-geist-mono">
          <span className={out.ok ? 'text-success' : 'text-danger'}>{out.status}</span>
          <span className="text-muted">{out.time}</span>
        </div>
        <ResultBlock text={truncate(bodyStr, 3000)} />
      </div>
    );
  }

  if (name === 'remember') {
    // Scope defaults to project tool-side; mirror that default here so the copy
    // never claims a cross-project save the tool didn't make.
    const isUserScope = String(part.input?.scope ?? 'project') === 'user';

    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-[11px]">
          <code className="bg-sunken border border-hairline px-1.5 py-0.5 rounded font-geist-mono text-secondary">
            {String(part.input?.key ?? '')}
          </code>
          <span className="text-faint uppercase tracking-[0.1em] text-[9.5px]">
            {String(part.input?.kind ?? 'fact')}
          </span>
        </div>
        {part.input?.content && (
          <div className="text-[12px] text-muted leading-[1.5]">{String(part.input.content)}</div>
        )}
        <div className="text-[10.5px] text-faint">
          {isUserScope
            ? 'Saved for all your projects — manage it in Account → Memory.'
            : 'Saved to this project only — manage it in Account → Memory.'}
        </div>
      </div>
    );
  }

  // Generic fallback: input + result, both captioned.
  return (
    <>
      {part.input && Object.keys(part.input).length > 0 && (
        <div>
          <div className="ui-caption mb-1.5">Input</div>
          <pre className="m-0 text-[12px] leading-[1.6] text-secondary font-geist-mono whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto modern-scrollbar">
            {truncate(JSON.stringify(part.input, null, 2), 3000)}
          </pre>
        </div>
      )}
      {result ? (
        <ResultBlock text={truncate(result.text, 3000)} isError={result.isError} />
      ) : (
        <div className="text-[12px] text-muted">Running…</div>
      )}
    </>
  );
}
